import React, { useState } from "react";
import { createProduct } from "../../../api/PupilApi";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

// --------------------- InputField Component ---------------------
const InputField = ({ field, value, onChange }) => {
  const baseClass =
    "w-full px-4 py-3 outline-none text-gray-800 placeholder-gray-500 text-sm bg-white rounded-xl border border-blue-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 shadow-sm";

  switch (field.type) {
    case "select":
      return (
        <select
          name={field.name}
          value={value || ""}
          onChange={onChange}
          className={`${baseClass} h-12`}
        >
          {field.options.map((option, index) => (
            <option key={index} value={option}>
              {option === "" ? `Select ${field.label}` : option}
            </option>
          ))}
        </select>
      );

    case "textarea":
      return (
        <textarea
          name={field.name}
          value={value || ""}
          onChange={onChange}
          placeholder={`Enter ${field.label.toLowerCase()}`}
          className={`${baseClass} min-h-[100px] resize-vertical`}
          rows="3"
        />
      );

    case "date":
      return (
        <input
          type="date"
          name={field.name}
          value={value || ""}
          onChange={onChange}
          className={`${baseClass} h-12`}
        />
      );

    default:
      return (
        <input
          type={field.type || "text"}
          name={field.name}
          value={value || ""}
          onChange={onChange}
          placeholder={`Enter ${field.label.toLowerCase()}`}
          className={`${baseClass} h-12`}
        />
      );
  }
};

// --------------------- Main Component ---------------------
const AddToProduct = () => {
  const navigate = useNavigate();

  const initialData = {
    category: "",
    subCategory: "",
    productName: "",
    description: "",
    purity: "",
    weight: "",
    pricePerGram: "",
    makingChargesPercentage: "",
    taxPercentage: "",
    designName: "",
    goldRateWhileOnboard: "",
    stockType: "",
    onboardDate: "",
    vendorName: "Palanigounder Jewellers",
    email: "support@grtjewels.com",
    contactNumber: "",
    billingAddress: "",
    shippingAddress: "",
    image1: "",
    image2: "",
    image3: "",
    videoUrl: "",
  };

  const [formData, setFormData] = useState(initialData);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    const jsonData = {
      productOnboarding: {
        productType: formData.category,
        brandName: "Palanigounder Jewellers",
        category: formData.category,
        subCategory: formData.subCategory,
        productDetails: {
          productName: formData.productName,
          description: formData.description,
          purity: formData.purity,
          weight: formData.weight,
          designName: formData.designName,
          hallmarkCertified: true,
          images: [
            { imageType: "front", imageUrl: formData.image1 },
            { imageType: "side", imageUrl: formData.image2 },
            { imageType: "modelView", imageUrl: formData.image3 },
          ],
          video: {
            videoType: "productDemo",
            videoUrl: formData.videoUrl,
          },
        },
        pricingDetails: {
          pricePerGram: Number(formData.pricePerGram),
          makingChargesPercentage: Number(formData.makingChargesPercentage),
          taxPercentage: Number(formData.taxPercentage),
          goldRateWhileOnboard: Number(formData.goldRateWhileOnboard),
        },
        vendorDetails: {
          vendorName: formData.vendorName,
          email: formData.email,
          contactNumber: formData.contactNumber,
          address: {
            billing: formData.billingAddress,
            shipping: formData.shippingAddress,
          },
        },
        stockType: formData.stockType,
        onboardDate: formData.onboardDate,
        createdBy: "Admin_Palanigounder_01",
      },
    };

    try {
      const res = await createProduct(jsonData);
      toast.success(res.data.message || "Product created successfully!");
      setFormData(initialData);
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => setFormData(initialData);
  const handleBack = () => navigate(-1);

  // --------------------- Form Sections ---------------------
  const formSections = [
    {
      title: "Product Basic Information",
      fields: [
        [
          { name: "category", label: "Category (Metal Type)", type: "select", options: ["", "Gold", "Silver"], colSpan: "md:col-span-1" },
          { name: "subCategory", label: "Sub Category", type: "select", options: ["", "Necklace", "Ring", "Stud", "Chain", "Bangle", "Bracelet", "Earring", "Pendant"], colSpan: "md:col-span-1" }
        ],
        [{ name: "productName", label: "Product Name", type: "text", colSpan: "md:col-span-2" }],
        [{ name: "description", label: "Product Description", type: "textarea", colSpan: "md:col-span-2" }],
      ]
    },
    {
      title: "Product Specifications",
      fields: [
        [
          { name: "purity", label: "Purity (e.g., 22K / 925 Silver)", type: "text", colSpan: "md:col-span-1" },
          { name: "weight", label: "Weight (grams)", type: "text", colSpan: "md:col-span-1" }
        ],
        [
          { name: "designName", label: "Design Name", type: "text", colSpan: "md:col-span-2 lg:col-span-1" },
          { name: "goldRateWhileOnboard", label: "Gold Rate While Onboard", type: "text", colSpan: "md:col-span-2 lg:col-span-1" }
        ]
      ]
    },
    {
      title: "Pricing Details",
      fields: [
        [
          { name: "pricePerGram", label: "Price per Gram (₹)", type: "text", colSpan: "md:col-span-1" },
          { name: "makingChargesPercentage", label: "Making Charges (%)", type: "text", colSpan: "md:col-span-1" }
        ],
        [{ name: "taxPercentage", label: "Tax (%)", type: "text", colSpan: "md:col-span-2 lg:col-span-1" }]
      ]
    },
    {
      title: "Inventory & Vendor Details",
      fields: [
        [
          { name: "stockType", label: "Stock Type", type: "select", options: ["", "inShop", "onlyView", "directSale"], colSpan: "md:col-span-1" },
          { name: "onboardDate", label: "Onboard Date", type: "date", colSpan: "md:col-span-1" }
        ],
        [{ name: "contactNumber", label: "Vendor Contact Number", type: "text", colSpan: "md:col-span-2 lg:col-span-1" }]
      ]
    },
    {
      title: "Media Assets",
      description: "Add Google Drive links for product images and videos",
      fields: [
        [
          { name: "image1", label: "Front View Image URL", type: "text", colSpan: "md:col-span-2 lg:col-span-1" },
          { name: "image2", label: "Side View Image URL", type: "text", colSpan: "md:col-span-2 lg:col-span-1" }
        ],
        [
          { name: "image3", label: "Model View Image URL", type: "text", colSpan: "md:col-span-2 lg:col-span-1" },
          { name: "videoUrl", label: "Product Video URL", type: "text", colSpan: "md:col-span-2 lg:col-span-1" }
        ]
      ]
    },
    {
      title: "Address Information",
      fields: [
        [{ name: "billingAddress", label: "Billing Address", type: "text", colSpan: "md:col-span-2" }],
        [{ name: "shippingAddress", label: "Shipping Address", type: "text", colSpan: "md:col-span-2" }]
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-yellow-50 p-3 sm:p-4 md:p-6 lg:p-8">
      <form
        onSubmit={handleSubmit}
        className="max-w-7xl mx-auto p-4 sm:p-6 md:p-8 lg:p-10 bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl border border-blue-100"
      >
        {/* Header */}
        <div className="text-center mb-6 md:mb-10 lg:mb-12">
          <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-600 to-yellow-600 bg-clip-text text-transparent mb-2 sm:mb-3">
            Palanigounder Jewellers – Add Product
          </h1>
          <p className="text-gray-600 text-xs sm:text-sm md:text-base">
            Add new gold or silver products to your inventory
          </p>
        </div>

        {error && (
          <div className="text-red-600 text-sm text-center bg-red-50 p-3 sm:p-4 rounded-2xl border border-red-200 mb-4 md:mb-6 lg:mb-8">
            {error}
          </div>
        )}

        {/* Form Sections */}
        <div className="space-y-6 md:space-y-8 lg:space-y-10">
          {formSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="bg-gray-50/80 rounded-2xl p-4 sm:p-6 md:p-8 border border-gray-200 overflow-hidden">
              <div className="mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-2">
                  {section.title}
                </h2>
                {section.description && (
                  <p className="text-gray-600 text-xs sm:text-sm">
                    {section.description}
                  </p>
                )}
              </div>
              <div className="space-y-4 sm:space-y-6">
                {section.fields.map((row, rowIndex) => (
                  <div key={rowIndex} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {row.map((field) => (
                      <div key={field.name} className={`flex flex-col space-y-2 ${field.colSpan || "md:col-span-1"}`}>
                        <label className="text-xs sm:text-sm font-semibold text-gray-700 ml-1">
                          {field.label}
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                        <InputField
                          field={field}
                          value={formData[field.name]}
                          onChange={handleChange}
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Buttons */}
        <div className="mt-6 md:mt-10 lg:mt-12 pt-6 border-t border-gray-200 flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-4">
          <button
            type="button"
            onClick={handleBack}
            className="order-2 lg:order-1 w-full lg:w-auto h-12 px-6 bg-gray-500 text-white rounded-full hover:bg-gray-600 transition-all duration-300 shadow-md hover:shadow-lg font-medium flex items-center justify-center"
          >
            <span className="mr-2">←</span> Back to Previous
          </button>
          <div className="order-1 lg:order-2 flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            <button
              type="button"
              onClick={handleReset}
              className="w-full sm:w-40 h-12 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all duration-300 shadow-md hover:shadow-lg font-medium flex items-center justify-center"
            >
              <span className="mr-2">↺</span> Reset All
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-48 h-12 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-md hover:shadow-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating Product...
                </>
              ) : (
                <>
                  <span className="mr-2">✓</span> Create Product
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddToProduct;
